import { Component, OnInit, signal } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { finalize } from "rxjs";
import { LogDetailsModalComponent } from "./log-details-modal/log-details-modal.component";
import {
  ILogApiItem,
  ILogRequest,
  LogLevel,
} from "../../../Utils/_models/logger.model";
import { SelectModule } from "primeng/select";
import { DatePicker, DatePickerModule } from "primeng/datepicker";
import { LoggerService } from "../../../Utils/_services/admin/logger/logger.service";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";
import { TooltipModule } from "primeng/tooltip";

@Component({
  selector: "app-logger",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    SpinnerLoaderComponent,
    SelectModule,
    SearchBoxComponent,
    DatePipe,
    LogDetailsModalComponent,
    TooltipModule
  ],
  templateUrl: "./logger.component.html",
  styleUrl: "./logger.component.css",
})
export class LoggerComponent implements OnInit {

  logs = signal<ILogApiItem[]>([]);
  allSelected: boolean = false;
  currentPage: number = 1;
  itemsPerPage: number = 3;
  totalEntries = signal(0);
  totalPages: number = 0;
  startIndex: number = 1;
  endIndex: number = 10;
  pageSizes: number[] = [5, 10, 20, 50, 100, 500, 1000];
  searchTerm: string | null = null;
  rangeDates: Date[] | undefined;
  loader = signal(false);
  logLevels: { label: string; value: string }[] = [];
  selectedLogLevel: LogLevel | null = null;
  selectedLog = signal<ILogApiItem | null>(null);
  isView = signal<boolean>(false);
  showDeleteBtn = signal(false);
  currentSortField = signal<
    "LevelName" | "Timestamp" |"OriginalUrl" | null
  >(null);
  currentSortOrder = signal<"asc" | "desc" | null>(null);
  includeDeleted = signal(false);
  hasSelection = signal(false);
  hasDeletedSelection = signal(false);

  constructor(
    private loggerService: LoggerService,
    private confirmationService: ConfirmService
  ) {}

  ngOnInit(): void {
    this.itemsPerPage = this.pageSizes[0];
    this.logLevels = Object.values(LogLevel).map(l => ({
      label: l,
      value: l,
    }));
    this.loadData();
  }

  onSearch(value: string | null) {
    this.currentPage = 1;
    this.searchTerm = value;
    this.loadData();
  }

  onDateFilter() {
    if (!this.rangeDates?.[0] || !this.rangeDates?.[1]) return;
    this.loadData();
  }

  formatLocalDate = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;

  loadData() {
    this.loader.set(true);
    const sortByBackend = this.currentSortField() === "OriginalUrl" ? null : this.currentSortField();
    const req: ILogRequest = {
      type: this.selectedLogLevel,
      page: this.currentPage,
      limit: this.itemsPerPage,
      startDate: !this.rangeDates?.[0]
        ? null
        : this.formatLocalDate(this.rangeDates[0]),
      endDate: !this.rangeDates?.[1]
        ? null
        : this.formatLocalDate(this.rangeDates[1]),
      search: this.searchTerm,
      sort: sortByBackend,
      sortOrder: sortByBackend ? this.currentSortOrder() : null,
      includeDeleted: this.includeDeleted()
    };

    this.loggerService
      .getAllLogs(req)
      .pipe(finalize(() => this.loader.set(false)))
      .subscribe({
        next: data => {
          let fetchedLogs = data.data.logs || [];
          if (this.currentSortField() === "OriginalUrl" && this.currentSortOrder()) {
            const direction = this.currentSortOrder() === "asc" ? 1 : -1;
            fetchedLogs = [...fetchedLogs].sort((a: any, b: any) => {
              const valA = (a.metadata?.originalUrl || '').toLowerCase();
              const valB = (b.metadata?.originalUrl || '').toLowerCase();
              if (valA < valB) return -1 * direction;
              if (valA > valB) return 1 * direction;
              return 0;
            });
          }
          this.logs.set(fetchedLogs);
          this.totalEntries.set(data.data.totalCount);
          this.updatePagination();
        },
        error: err => {
          console.error("Error fetching logs:", err);
        },
      });
  }

  onSort(
    field: "LevelName" | "Timestamp" |"OriginalUrl"
  ): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.loadData();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }

  updateSelectAllState() {
    const all = this.logs();
    const selectedLogs = all.filter(x => x.selected);
    this.allSelected = all.length > 0 && selectedLogs.length === all.length;
    this.hasSelection.set(selectedLogs.length > 0);
    this.hasDeletedSelection.set(selectedLogs.some(x => !!(x as any).deletedAt));
  }

  toggleSelectAll() {
    this.allSelected = !this.allSelected;
    this.logs().forEach(x => {
      x.selected = this.allSelected;
    });
    this.updateSelectAllState();
  }

  toggleIncludeDeleted() {
    this.includeDeleted.update(v => !v);
    this.currentPage = 1;
    this.loadData();
  }

  async deleteSelectedLogs() {
    const selectedLogs = this.logs().filter(x => x.selected);
    if (selectedLogs.length === 0) return;

    const deletedSelected = selectedLogs.filter(x => (x as any).deletedAt);
    const activeSelected = selectedLogs.filter(x => !(x as any).deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? 'Restore Logs' : 'Delete Logs';
    const bodyHeader = isRestore ? 'Restore Selected Logs' : 'Delete Selected Logs';
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted log(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted log(s) will also be deleted.` : ''}`
      : `Are you sure you want to delete ${activeSelected.length} selected log(s)? This action can be undone later if "Include Deleted" is checked.`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? 'Apply Changes'
        : isRestore
          ? 'Restore'
          : 'Delete';
    const yesBtnCss = isRestore ? 'btn-primary' : 'btn-danger';

    const confirmed = await this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      yesBtnCss,
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });

    if (confirmed) {
      this.loggerService
        .deleteLogs(selectedLogs.map(x => x._id))
        .subscribe({
          next: res => {
            this.loadData();
          },
        });
    }
  }

  onPageSizeChange(newSize: string) {
    this.itemsPerPage = +newSize;
    this.currentPage = 1;
    this.updatePagination();
    this.loadData();
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
      this.loadData();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
      this.loadData();
    }
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.totalEntries() / this.itemsPerPage);
    this.startIndex = (this.currentPage - 1) * this.itemsPerPage + 1;
    this.endIndex = Math.min(
      this.currentPage * this.itemsPerPage,
      this.totalEntries()
    );
    this.updateSelectAllState();
  }

  viewDetails(log: ILogApiItem) {
    this.selectedLog.set(log);
    this.isView.set(true);
  }

  closeViewDetails() {
    this.selectedLog.set(null);
    this.isView.set(false);
  }
}
